import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSync, FaExclamationTriangle, FaCheckCircle, FaTimesCircle, FaSearch } from 'react-icons/fa';
import { debounce } from 'lodash';
import { listRCTIs, createManualRCTI } from '../services/rctiService';
import { searchFranchisees } from '../services/franchiseeService';
import { formatDate } from '../utils/dateUtils';
import XeroStatusBadge from './XeroStatusBadge';

const GST_RATE = 0.1;
const PAGE_SIZE = 20;

const formatCurrency = (value) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? 'N/A'
    : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(value));

const round2 = (value) => Number(Number(value).toFixed(2));

const ManualRCTITabContent = () => {
  const navigate = useNavigate();
  const [rctis, setRctis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Create form state
  const [fseSearch, setFseSearch] = useState('');
  const [fseSuggestions, setFseSuggestions] = useState([]);
  const [showFseSuggestions, setShowFseSuggestions] = useState(false);
  const [selectedFse, setSelectedFse] = useState(null);
  const [description, setDescription] = useState('');
  const [amountExGst, setAmountExGst] = useState('');
  const [ndisJob, setNdisJob] = useState(false);
  const [formError, setFormError] = useState(null);

  const fseBoxRef = useRef(null);

  const fetchRctis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listRCTIs({ manual: true, pageSize: PAGE_SIZE, orderBy: 'createdAt', orderDirection: 'desc' });
      setRctis(data?.rctis || []);
    } catch (err) {
      setError(err.message || 'Failed to load manual RCTIs.');
      setRctis([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRctis(); }, [fetchRctis]);

  const debouncedFseSearch = useCallback(
    debounce(async (term) => {
      if (term.trim().length < 3) {
        setFseSuggestions([]);
        setShowFseSuggestions(false);
        return;
      }
      try {
        const data = await searchFranchisees({ searchString: term });
        setFseSuggestions(data?.franchisees || []);
        setShowFseSuggestions(true);
      } catch (err) {
        console.error('Franchisee search failed:', err);
        setFseSuggestions([]);
      }
    }, 300),
    []
  );

  useEffect(() => {
    debouncedFseSearch(fseSearch);
    return () => debouncedFseSearch.cancel();
  }, [fseSearch, debouncedFseSearch]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (fseBoxRef.current && !fseBoxRef.current.contains(e.target)) setShowFseSuggestions(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const amount = Number(amountExGst);
  const amountValid = Number.isFinite(amount) && amount > 0;
  // NDIS work is GST free, matching the TaxType the backend stamps on the RCTI
  const gst = amountValid ? (ndisJob ? 0 : round2(amount * GST_RATE)) : null;
  const totalIncGst = amountValid ? round2(amount + (gst || 0)) : null;

  const resetForm = () => {
    setFseSearch('');
    setSelectedFse(null);
    setFseSuggestions([]);
    setShowFseSuggestions(false);
    setDescription('');
    setAmountExGst('');
    setNdisJob(false);
    setFormError(null);
  };

  const handleOpenForm = () => {
    resetForm();
    setResult(null);
    setIsFormOpen(true);
  };

  const handleReviewClick = () => {
    if (!selectedFse) return setFormError('Select a franchisee.');
    // Blocked here as well as server-side: an RCTI cannot be issued without a supplier ABN,
    // and finding out after the confirmation would have already consumed an RCTI number.
    if (!selectedFse.abn) return setFormError(`${selectedFse.franchiseName || 'That franchisee'} has no ABN on record, which an RCTI requires.`);
    if (!description.trim()) return setFormError('Enter a description.');
    if (!amountValid) return setFormError('Enter an amount greater than zero.');
    setFormError(null);
    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await createManualRCTI({
        fseId: selectedFse.id,
        description: description.trim(),
        amountExGst: amount,
        ndisJob,
      });
      setResult(res);
      setIsConfirmOpen(false);
      setIsFormOpen(false);
      resetForm();
      await fetchRctis();
    } catch (err) {
      setFormError(err.message || 'Failed to create the RCTI.');
      setIsConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const stepIcon = (status) =>
    status === 'success'
      ? <FaCheckCircle className="text-green-600 inline mr-1" />
      : status === 'skipped'
        ? <FaExclamationTriangle className="text-yellow-600 inline mr-1" />
        : <FaTimesCircle className="text-red-600 inline mr-1" />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Manual RCTIs</h2>
          <p className="text-sm text-gray-500">RCTIs raised directly for a franchisee, not generated from a job.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchRctis}
            disabled={loading}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center disabled:opacity-50"
          >
            <FaSync className={`mr-1 ${loading ? 'animate-spin' : ''}`} size={12} /> Refresh
          </button>
          <button
            onClick={handleOpenForm}
            className="px-3 py-1.5 text-sm font-medium text-white bg-jims-cyan rounded-md hover:bg-jims-cyan-dark inline-flex items-center"
          >
            <FaPlus className="mr-1" size={12} /> Create RCTI
          </button>
        </div>
      </div>

      {/* Per-step outcome: the RCTI exists once created, so a failed email or Xero push is
          reported alongside the success rather than replacing it. */}
      {result && (
        <div className="mb-4 p-4 bg-white border border-gray-300 rounded-md">
          <p className="font-medium text-gray-900 mb-2">
            Created {result.rctiNumber}
          </p>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>{stepIcon(result.pdf?.status)} PDF: {result.pdf?.status}{result.pdf?.message ? ` — ${result.pdf.message}` : ''}</li>
            <li>{stepIcon(result.email?.status)} Email to franchisee: {result.email?.status}{result.email?.message ? ` — ${result.email.message}` : ''}</li>
            <li>{stepIcon(result.xero?.status)} Xero: {result.xero?.status}{result.xero?.message ? ` — ${result.xero.message}` : ''}</li>
          </ul>
          <button onClick={() => setResult(null)} className="mt-3 text-sm text-gray-500 hover:text-gray-700 underline">
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">{error}</div>
      )}

      {loading ? (
        <p className="text-center text-gray-500 p-6 bg-white rounded shadow">Loading manual RCTIs...</p>
      ) : rctis.length === 0 ? (
        <p className="text-center text-gray-500 p-6 bg-white rounded shadow">No manual RCTIs yet.</p>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full leading-normal">
            <thead>
              <tr className="bg-gray-100 text-left text-gray-600 uppercase text-xs">
                <th className="px-5 py-3 border-b-2 border-gray-200">RCTI No.</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Franchisee</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Description</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Date</th>
                <th className="px-5 py-3 border-b-2 border-gray-200 text-right">Amount</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Status</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Xero</th>
              </tr>
            </thead>
            <tbody>
              {rctis.map((rcti) => (
                // The shared RCTI details page already copes with a jobless RCTI, so a manual
                // one gets PDF download, resend, void and Xero sync for free.
                <tr
                  key={rcti.id}
                  onClick={() => navigate(`/rctis/${rcti.id}`, { state: { returnSearch: '?tab=manual-rctis' } })}
                  className="hover:bg-gray-50 text-sm cursor-pointer"
                >
                  <td className="px-5 py-4 border-b border-gray-200 whitespace-nowrap">{rcti.RCTINumber || 'N/A'}</td>
                  <td className="px-5 py-4 border-b border-gray-200">
                    <p className="text-gray-900">{rcti.SupplierName || 'N/A'}</p>
                    <p className="text-xs text-gray-500">{rcti.franchiseeId || ''}</p>
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200 max-w-md">
                    <p className="text-gray-700 truncate" title={rcti.Description}>{rcti.Description || 'N/A'}</p>
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200 whitespace-nowrap">{formatDate(rcti.RCTIDate)}</td>
                  <td className="px-5 py-4 border-b border-gray-200 text-right whitespace-nowrap">
                    {formatCurrency(rcti.UnitAmount)}
                    <span className="block text-xs text-gray-500">inc. GST</span>
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      {rcti.status === 'notified' ? 'FSE Notified' : (rcti.status || 'N/A')}
                    </span>
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200">
                    <XeroStatusBadge
                      submissionStatus={rcti.xero_submission_status}
                      invoiceStatus={rcti.xero_invoice_status}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create form */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create Manual RCTI</h3>

            <div ref={fseBoxRef} className="relative mb-4">
              <label htmlFor="manual-rcti-fse" className="block text-sm font-medium text-gray-700">Franchisee</label>
              <div className="relative mt-1">
                <input
                  id="manual-rcti-fse"
                  type="text"
                  value={fseSearch}
                  onChange={(e) => { setFseSearch(e.target.value); setSelectedFse(null); }}
                  placeholder="Type at least 3 characters"
                  autoComplete="off"
                  className="block w-full px-3 py-2 pr-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                />
                <FaSearch className="absolute right-3 top-3 text-gray-400" size={12} />
              </div>
              {showFseSuggestions && fseSuggestions.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto">
                  {fseSuggestions.map((fse) => (
                    <li key={fse.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFse(fse);
                          setFseSearch(`${fse.franchiseName || 'Unnamed'}${fse.fseCode ? ` (${fse.fseCode})` : ''}`);
                          setShowFseSuggestions(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                      >
                        {fse.franchiseName || 'Unnamed'} {fse.fseCode ? `(${fse.fseCode})` : ''}
                        {!fse.abn && <span className="ml-2 text-xs text-red-600">no ABN</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {selectedFse && (
                <p className="mt-1 text-xs text-gray-500">
                  {selectedFse.email || 'No email on record'} · ABN {selectedFse.abn || 'missing'}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="manual-rcti-desc" className="block text-sm font-medium text-gray-700">Invoice description</label>
              <textarea
                id="manual-rcti-desc"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this RCTI is for"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="manual-rcti-amount" className="block text-sm font-medium text-gray-700">Amount (ex. GST)</label>
              <input
                id="manual-rcti-amount"
                type="number"
                step="0.01"
                min="0"
                value={amountExGst}
                onChange={(e) => setAmountExGst(e.target.value)}
                placeholder="100.00"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm
                  [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              {amountValid && (
                <p className="mt-1 text-sm text-gray-600">
                  GST {formatCurrency(gst)} · Total {formatCurrency(totalIncGst)}
                  {ndisJob && <span className="text-gray-500"> (NDIS, GST free)</span>}
                </p>
              )}
            </div>

            <label className="inline-flex items-center mb-4">
              <input
                type="checkbox"
                checked={ndisJob}
                onChange={(e) => setNdisJob(e.target.checked)}
                className="h-4 w-4 text-jims-cyan border-gray-300 rounded focus:ring-jims-cyan"
              />
              <span className="ml-2 text-sm text-gray-700">NDIS (GST free)</span>
            </label>

            {formError && <p className="mb-3 text-sm text-red-600">{formError}</p>}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setIsFormOpen(false); resetForm(); }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReviewClick}
                className="px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation: this step emails the franchisee and pushes to Xero, neither of which
          can be undone from here, so the consequences are spelled out before proceeding. */}
      {isConfirmOpen && selectedFse && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Create and send this RCTI?</h3>
            <dl className="text-sm text-gray-700 space-y-1 mb-4">
              <div><dt className="inline font-medium">Franchisee: </dt><dd className="inline">{selectedFse.franchiseName || selectedFse.fseCode}</dd></div>
              <div><dt className="inline font-medium">Email: </dt><dd className="inline">{selectedFse.email || 'none on record'}</dd></div>
              <div><dt className="inline font-medium">Description: </dt><dd className="inline">{description.trim()}</dd></div>
              <div><dt className="inline font-medium">Amount: </dt><dd className="inline">{formatCurrency(amount)} ex. GST</dd></div>
              <div><dt className="inline font-medium">GST: </dt><dd className="inline">{formatCurrency(gst)}</dd></div>
              <div><dt className="inline font-medium">Total: </dt><dd className="inline font-semibold">{formatCurrency(totalIncGst)}</dd></div>
            </dl>
            <div className="bg-yellow-50 border border-yellow-300 rounded p-3 mb-4">
              <p className="text-sm text-yellow-800">
                This creates the RCTI and its PDF, emails it to the franchisee, and sends it to Xero.
                It cannot be undone from this screen.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsConfirmOpen(false)}
                disabled={submitting}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting}
                className="px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark disabled:opacity-50"
              >
                {submitting ? 'Creating...' : 'Confirm and send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualRCTITabContent;
